import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { useI18n } from '../context/I18nContext';
import { CheckCircle2, Loader2 } from 'lucide-react';

export default function CheckoutSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('processing');
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.status === 'expired') { setStatus('expired'); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setStatus('timeout'); return; }
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= 8) { setStatus('error'); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 lg:py-32 text-center" data-testid="checkout-success-page">
      {status === 'processing' && (
        <>
          <Loader2 size={48} className="text-[#c9a961] mx-auto mb-8 animate-spin" />
          <h1 className="font-serif text-4xl text-[#f5f5f0] mb-4">{t('checkout.processing')}</h1>
        </>
      )}
      {status === 'paid' && (
        <>
          <CheckCircle2 size={64} className="text-[#c9a961] mx-auto mb-8" strokeWidth={1.2} />
          <h1 className="font-serif text-5xl text-[#f5f5f0] mb-4">{t('checkout.success_t')}</h1>
          <p className="text-lg text-[#a3b5ad] font-light mb-10">{t('checkout.success_b')}</p>
          {details?.amount_total && (
            <p className="font-serif text-3xl text-[#c9a961] mb-10">
              {new Intl.NumberFormat('it-CH', { style: 'currency', currency: (details.currency || 'EUR').toUpperCase(), maximumFractionDigits: 0 }).format(details.amount_total / 100)}
            </p>
          )}
          <Link to="/" className="gn-btn-primary">{t('checkout.back_home')}</Link>
        </>
      )}
      {(status === 'expired' || status === 'error') && (
        <>
          <h1 className="font-serif text-4xl text-[#f5f5f0] mb-4">{t('checkout.cancel_t')}</h1>
          <p className="text-lg text-[#a3b5ad] mb-10">{t('checkout.cancel_b')}</p>
          <Link to="/catalog" className="gn-btn-primary">{t('checkout.back_catalog')}</Link>
        </>
      )}
      {status === 'timeout' && (
        <>
          <h1 className="font-serif text-4xl text-[#f5f5f0] mb-4">{t('checkout.processing')}</h1>
          <p className="text-lg text-[#a3b5ad] mb-10">{t('checkout.timeout')}</p>
          <Link to="/" className="gn-btn-primary">{t('checkout.back_home')}</Link>
        </>
      )}
    </div>
  );
}
